import { useCallback, useRef, useState } from 'react'
import { URL } from '../gql/client'

export interface LatencyResult {
  wsRtt: number | null
  httpRtt: number | null
  measuredAt: number | null
  measuring: boolean
  error: string | null
}

export const useLatency = (socket: WebSocket | null) => {
  const [result, setResult] = useState<LatencyResult>({
    wsRtt: null,
    httpRtt: null,
    measuredAt: null,
    measuring: false,
    error: null,
  })
  const pendingRef = useRef<{
    timeoutId: ReturnType<typeof setTimeout>
    sentAt: number
  } | null>(null)

  const measureWs = useCallback((ws: WebSocket): Promise<number> => {
    return new Promise((resolve, reject) => {
      const onMessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data)
          if (data.type !== 'pong') return
          ws.removeEventListener('message', onMessage)
          const pending = pendingRef.current
          if (!pending) return
          clearTimeout(pending.timeoutId)
          pendingRef.current = null
          resolve(Date.now() - pending.sentAt)
        } catch {
          // non-JSON messages on shared socket — ignore
        }
      }
      const sentAt = Date.now()
      const timeoutId = setTimeout(() => {
        ws.removeEventListener('message', onMessage)
        pendingRef.current = null
        reject(new Error('WebSocket ping timed out'))
      }, 5000)
      pendingRef.current = { timeoutId, sentAt }
      ws.addEventListener('message', onMessage)
      ws.send(JSON.stringify({ type: 'ping', ts: sentAt }))
    })
  }, [])

  const measureHttp = useCallback(async (): Promise<number> => {
    const start = performance.now()
    const res = await fetch(`${URL}/health/ping`)
    if (!res.ok) throw new Error(`HTTP ping failed: ${res.status}`)
    return Math.round(performance.now() - start)
  }, [])

  const measure = useCallback(async () => {
    if (result.measuring) return
    setResult((p) => ({ ...p, measuring: true, error: null }))

    let wsRtt: number | null = null
    let httpRtt: number | null = null
    let error: string | null = null

    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        wsRtt = await measureWs(socket)
      } catch (e) {
        error = e instanceof Error ? e.message : 'WS ping failed'
      }
    } else {
      error = 'WebSocket not connected'
    }

    try {
      httpRtt = await measureHttp()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'HTTP ping failed'
      error = error ? `${error}; ${msg}` : msg
    }

    setResult({ wsRtt, httpRtt, measuredAt: Date.now(), measuring: false, error })
  }, [socket, result.measuring, measureWs, measureHttp])

  return { result, measure }
}
